// src/components/reports/ReportTemplate.jsx
//
// Shared building blocks for all Takso report templates.
// Import from here into any report page (single-activity, single-program,
// multi-program) to guarantee a consistent look and feel across all formats.
//
// Ported from takso-poc/takso-reporting-ui/src/components/ReportTemplate.jsx.
// Inline style objects are intentional here — they are the canonical style source
// shared across report types. Page-specific layout additions go in the nearest
// .scss file.
//
// ── Exports ──────────────────────────────────────────────────────────────────
//
//  Typography
//    REPORT_FONT              Scale object (px numbers)
//
//  Section structure
//    CollapsibleSection       H1 section heading with open/close toggle
//    SubHeading               H2 sub-section label
//    ChartTitle               H3 above a chart (accepts headingStyle prop)
//    TableLabel               H3 above a data table
//
//  Content blocks
//    DataPanel                Green tint (toggle-gated) — non-editable DB values
//    WriterNote               Plain editable container for author guidance
//    WriterNoteHtml           CKEditor-backed alternative — persists real HTML
//    AuthorComment            Amber note inside a WriterNote
//    ChartProse               Grey editable figure-caption box below a chart
//    ChartSlot                Placeholder shown before a chart is included
//    ChartCaption             Amber editable caption below an included chart
//
//  Small helpers
//    Stat                     Label + bold value pair (DataPanel summary grids)
//    LoadingPanel             Loading / no-data fallback block
//
//  Table cell styles (plain objects, spread into style={})
//    thStyle / thStyleR / thStyleC   Header cells — left / right / centre
//    tdStyle / tdStyleR / tdStyleC   Body cells   — left / right / centre

import { useState, useEffect, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { renderToStaticMarkup } from 'react-dom/server';

import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  ClassicEditor,
  Bold,
  Essentials,
  Italic,
  Paragraph,
  List,
} from 'ckeditor5';

import { HtmlService } from '../../services';
import { ReportTintContext } from './ReportTintContext';

import './ReportTemplate.scss';

// ── Typography scale ──────────────────────────────────────────────────────────
//
//  Level       Size  Weight  Element
//  ─────────── ────  ──────  ──────────────────────────────────────────
//  title        20    700    Org name / report title
//  h1           16    700    Section headings (CollapsibleSection)
//  h2           14    700    Sub-headings (SubHeading)
//  h3           12    600    Table / chart labels (ChartTitle, L3Heading)
//  body         14    400    Normal body text
//  th           10    600    Table column header cells
//  td           12    400    Table body cells
//  note         10    400    Footnotes, captions
//
// Tint palette constants
// Category 1 — template structure (headings H1/H2/H3, TOC): amber (50% tone)
export const TINT_TEMPLATE_BG = '#fff9e3'; // amber-100 at 50% over white
// Category 2 — non-editable DB data (DataPanel, tables): green
const TINT_DB_BG = '#f0fdf4'; // green-50

export const REPORT_FONT = {
  title: 20, // Org name / report title
  h1: 18, // Section headings (CollapsibleSection)
  h2: 16, // Sub-headings (SubHeading)
  h3: 14, // Chart / table labels (L3Heading, ChartTitle)
  body: 14, // Normal body text
  th: 12, // Table column header cells
  td: 12, // Table body cells
  note: 10, // Footnotes, captions
};

// Font weights paired to each level — use alongside REPORT_FONT sizes.
export const REPORT_WEIGHT = {
  title: 700,
  h1: 700,
  h2: 700,
  h3: 600,
  body: 400,
  th: 600,
  td: 400,
  note: 400,
};

// ── Section structure ─────────────────────────────────────────────────────────

export const CollapsibleSection = ({
  title,
  children,
  forceBreak,
  anchorId,
}) => {
  const showTint = useContext(ReportTintContext);
  return (
    <div
      id={anchorId || undefined}
      style={{ marginTop: 28, breakInside: 'avoid' }}
      {...(forceBreak ? { 'data-force-break': true } : {})}
    >
      <div
        data-break-guard="h1"
        style={{
          fontFamily: 'inherit',
          fontSize: REPORT_FONT.h1,
          fontWeight: REPORT_WEIGHT.h1,
          color: '#111827',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          marginBottom: 12,
          background: showTint ? TINT_TEMPLATE_BG : 'transparent',
          borderRadius: showTint ? 3 : 0,
          padding: showTint ? '3px 6px' : 0,
          breakAfter: 'avoid',
          pageBreakAfter: 'avoid',
        }}
      >
        {title}
      </div>
      <div>{children}</div>
    </div>
  );
};

export const SubHeading = ({ children }) => {
  const showTint = useContext(ReportTintContext);
  return (
    <div
      data-break-guard="h2"
      style={{
        fontFamily: 'inherit',
        fontSize: REPORT_FONT.h2,
        fontWeight: REPORT_WEIGHT.h2,
        color: '#111827',
        marginTop: 16,
        marginBottom: 8,
        background: showTint ? TINT_TEMPLATE_BG : 'transparent',
        borderRadius: showTint ? 3 : 0,
        padding: showTint ? '2px 6px' : 0,
        breakAfter: 'avoid',
        pageBreakAfter: 'avoid',
      }}
    >
      {children}
    </div>
  );
};

const HEADING_VISUAL = {
  'Heading 2': {
    fontSize: REPORT_FONT.h2,
    fontWeight: 700,
    fontStyle: 'normal',
  },
  'Heading 3': {
    fontSize: REPORT_FONT.h3,
    fontWeight: 700,
    fontStyle: 'normal',
  },
};

export const ChartTitle = ({ title, headingStyle = 'Heading 3' }) => {
  const showTint = useContext(ReportTintContext);
  const s = HEADING_VISUAL[headingStyle] || HEADING_VISUAL['Heading 3'];
  return (
    <div
      data-break-guard="h3"
      style={{
        ...s,
        color: '#111827',
        marginBottom: 5,
        marginTop: 16,
        background: showTint ? TINT_TEMPLATE_BG : 'transparent',
        borderRadius: showTint ? 3 : 0,
        padding: showTint ? '2px 6px' : 0,
        breakAfter: 'avoid',
        pageBreakAfter: 'avoid',
      }}
    >
      {title}
    </div>
  );
};

export const TableLabel = ({ title }) => {
  const showTint = useContext(ReportTintContext);
  return (
    <div
      data-break-guard="h3"
      style={{
        fontFamily: 'inherit',
        fontSize: REPORT_FONT.h3,
        fontWeight: 600,
        color: '#374151',
        marginBottom: 5,
        marginTop: 16,
        background: showTint ? TINT_TEMPLATE_BG : 'transparent',
        borderRadius: showTint ? 3 : 0,
        padding: showTint ? '2px 6px' : 0,
        breakAfter: 'avoid',
        pageBreakAfter: 'avoid',
      }}
    >
      {title}
    </div>
  );
};

// ── Content blocks ────────────────────────────────────────────────────────────

export const DataPanel = ({ children, tight }) => {
  const showTint = useContext(ReportTintContext);
  return (
    <div
      style={{
        background: showTint ? TINT_DB_BG : 'transparent',
        border: `1px solid ${showTint ? TINT_DB_BG : 'transparent'}`,
        borderRadius: 5,
        padding: tight ? '10px 14px 10px 0' : '14px 18px 14px 0',
        fontSize: 'var(--doc-body-size, 14px)',
        color: '#111827',
        marginBottom: 10,
        lineHeight: 1.65,
        breakInside: 'avoid',
        pageBreakInside: 'avoid',
      }}
    >
      {children}
    </div>
  );
};

export const WriterNote = ({
  children,
  onBlur,
  richText,
  saveAsSimpleText = false,
  emptyPlaceholder,
  emptyPlaceholderClassName = 'wn-empty-placeholder',
  debug = false,
}) => {
  const { t } = useTranslation();

  // Capture initial HTML once on mount via renderToStaticMarkup.
  // Using dangerouslySetInnerHTML means React never tracks DOM nodes inside
  // the contentEditable div, preventing the removeChild reconciliation error
  // that occurs when surrounding content re-renders after user editing.

  if (debug) {
    debugger;
  }

  const hasContent = HtmlService.hasContent(children);
  const initialHtmlRef = useRef(null);
  if (initialHtmlRef.current === null) {
    initialHtmlRef.current = HtmlService.convertToInnerHtml({
      children,
      richText,
    });
  }

  return (
    <div
      contentEditable
      suppressContentEditableWarning
      dangerouslySetInnerHTML={{ __html: initialHtmlRef.current }}
      onPaste={
        richText
          ? (e) => {
              e.preventDefault();
              const html = e.clipboardData.getData('text/html');
              const text = e.clipboardData.getData('text/plain');

              /***
              if (text) {
                const sm = renderToStaticMarkup(<>{text}</>);
                debugger;
              }
              ***/

              const insert = html
                ? HtmlService.sanitizePastedHtml(html)
                : HtmlService.simpleStringToHtml(text);
              document.execCommand('insertHTML', false, insert);
            }
          : undefined
      }
      onBlur={
        onBlur
          ? (e) => {
              const el = e.currentTarget;
              const text =
                richText && saveAsSimpleText
                  ? HtmlService.htmlToPlainText(el)
                  : richText
                    ? el.innerHTML
                    : el.innerText;
              onBlur(text.trim());
            }
          : undefined
      }
      // CSS-only placeholder (:empty::before) rather than a real styled child
      // node — a real node's inline style otherwise survives select-all+type
      // (browsers replace only the text inside it), baking the placeholder's
      // grey/italic style into whatever the user actually saves.
      data-placeholder={!hasContent ? emptyPlaceholder : undefined}
      className={
        !hasContent && emptyPlaceholder ? emptyPlaceholderClassName : undefined
      }
      data-break-avoid
      style={{
        padding: '4px 16px 4px 0',
        fontFamily: 'inherit',
        fontSize: `var(--doc-body-size, ${REPORT_FONT.body}px)`,
        fontWeight: REPORT_WEIGHT.body,
        color: '#111827',
        marginTop: hasContent ? 16 : 0,
        marginBottom: hasContent ? 10 : 0,
        lineHeight: 1.7,
        cursor: 'text',
        outline: 'none',
        breakInside: 'avoid',
        pageBreakInside: 'avoid',
      }}
      title={t('actions.click_to_edit')}
    />
  );
};

// CKEditor-backed alternative to WriterNote, for fields that should persist
// real HTML rather than the tagless/legacy-plain-text shapes WriterNote
// supports. Same toolbar as CKField (src/containers/activities/form/
// reportFields.js), defined locally rather than imported from there so this
// components/ file doesn't reach into containers/.
const CK_TOOLBAR_CONFIG = {
  plugins: [Essentials, Bold, Italic, List, Paragraph],
  toolbar: ['bold', 'italic', 'bulletedList', 'numberedList'],
};

// children is already-resolved HTML — callers are expected to run it through
// HtmlService.convertToInnerHtml() first (so legacy plain text becomes
// <p>-based HTML, and already-HTML content passes through normalized), unlike
// WriterNote which does that detection/conversion itself. Captured once on
// mount into a ref, same uncontrolled pattern as WriterNote/CKField, so
// CKEditor owns its own state and a parent re-render can't fight it.
export const WriterNoteHtml = ({ children, onBlur, emptyPlaceholder }) => {
  const initialDataRef = useRef(null);
  if (initialDataRef.current === null) {
    initialDataRef.current = children || '';
  }
  const editorDataRef = useRef(initialDataRef.current);
  const onBlurRef = useRef(onBlur);
  onBlurRef.current = onBlur;

  // Save is driven by CKEditor's internal focus tracking — the editable
  // surface lives inside CKEditor's own view, so a DOM onBlur on a wrapper
  // never fires. Also flush on unmount: switching tabs unmounts this
  // component directly without ever firing that blur, which would otherwise
  // silently discard a pending edit.
  useEffect(() => {
    return () => {
      if (
        onBlurRef.current &&
        editorDataRef.current !== initialDataRef.current
      ) {
        onBlurRef.current(editorDataRef.current);
      }
    };
  }, []);

  return (
    <div className="rt-ck-field" data-break-avoid>
      <CKEditor
        editor={ClassicEditor}
        config={{ ...CK_TOOLBAR_CONFIG, placeholder: emptyPlaceholder }}
        data={initialDataRef.current}
        onChange={(event, editor) => {
          editorDataRef.current = editor.getData();
        }}
        onReady={(editor) => {
          // editor.editing.view.document's own 'blur' event fires the
          // instant the editable content area loses focus — including when
          // focus moves to this same editor's toolbar (e.g. clicking Bold
          // after selecting text), which reads as "left the field" and
          // fires a save mid-edit, closing the toolbar under the user's
          // cursor. editor.ui.focusTracker treats the editable and its
          // toolbar as one region and only reports unfocused once focus has
          // genuinely left both (with its own built-in debounce for the
          // editable↔toolbar handoff) — the CKEditor-documented way to
          // detect "the user left the editor" without that false positive.
          editor.ui.focusTracker.on(
            'change:isFocused',
            (evt, name, isFocused) => {
              if (!isFocused) {
                onBlurRef.current?.(editorDataRef.current);
              }
            }
          );
        }}
      />
    </div>
  );
};

// SyncedField — wraps any contentEditable block that saves back to the DB.
// Grey background (#f9fafb) + ↺ badge in the left margin signal two-way sync.
// Always visible regardless of the amber/green tint toggle (that toggle controls
// non-editable template/DB colours only). Author-only WriterNote stays plain white.
//
// ↺ placement: centred vertically in the block. If the block is taller than one
// A4 page (1123px at 96dpi), a second badge is added centred in the second-page
// portion, and so on.
const A4_PAGE_H = 1123;

export const SyncedField = ({ children, style }) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const [badgeCount, setBadgeCount] = useState(1);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    // Debounced — see the matching comment on PageBreaks' ResizeObserver in
    // ReportPreview.jsx. This one sits even closer to the editable field
    // (it's SyncedField's own direct wrapper), so an un-debounced
    // setBadgeCount firing in the same tick as a WriterNote paragraph split
    // is just as likely to interrupt the browser's caret placement.
    let debounceTimer = null;
    const observer = new ResizeObserver(([entry]) => {
      const h = entry.contentRect.height;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        setBadgeCount(Math.max(1, Math.ceil(h / A4_PAGE_H)));
      }, 250);
    });
    observer.observe(el);
    return () => {
      clearTimeout(debounceTimer);
      observer.disconnect();
    };
  }, []);

  return (
    <div ref={containerRef} style={{ position: 'relative', ...style }}>
      {Array.from({ length: badgeCount }, (_, i) => (
        <span
          key={i}
          title={t(
            'report.builder.preview.edits_here_update_the_activity_record'
          )}
          style={{
            position: 'absolute',
            left: -24,
            top: `${((i + 0.5) / badgeCount) * 100}%`,
            transform: 'translateY(-50%)',
            fontSize: 16,
            fontWeight: 700,
            color: '#ef8123',
            cursor: 'default',
            userSelect: 'none',
            lineHeight: 1,
          }}
        >
          ↺
        </span>
      ))}
      <div
        style={{
          background: '#f9fafb',
          border: '1px solid #f0f0f0',
          borderRadius: 6,
          padding: '4px 12px 4px 0',
        }}
      >
        {children}
      </div>
    </div>
  );
};

export const AuthorComment = ({ children }) => (
  <div
    style={{
      fontStyle: 'italic',
      fontSize: REPORT_FONT.h3,
      background: '#fef3c7',
      border: '1px solid #fcd34d',
      borderRadius: 4,
      padding: '8px 8px 8px 0',
      color: '#111827',
      marginBottom: 8,
      lineHeight: 1.7,
    }}
  >
    {children}
  </div>
);

export function ChartProse({ figN, defaultText, onChange }) {
  const { t } = useTranslation();
  const [text, setText] = useState(defaultText || '');
  const prevDefaultRef = useRef(defaultText || '');
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    const next = defaultText || '';
    if (next === prevDefaultRef.current) return;
    const prev = prevDefaultRef.current;
    prevDefaultRef.current = next;
    setText((t) => (t === prev ? next : t));
  }, [defaultText]);

  useEffect(() => {
    onChangeRef.current?.(text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  return (
    <div
      style={{
        marginTop: 8,
        marginBottom: 10,
        background: '#f9fafb',
        border: '1px solid #f9fafb',
        borderRadius: 6,
        padding: '10px 14px 10px 0',
      }}
    >
      {figN != null && (
        <div
          style={{
            fontSize: 'var(--doc-body-size, 14px)',
            fontWeight: 700,
            color: '#374151',
            marginBottom: 3,
          }}
        >
          {t('charts.figure')} {figN}.
        </div>
      )}
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={4}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          display: 'block',
          fontSize: 'var(--doc-body-size, 14px)',
          lineHeight: 1.6,
          padding: 0,
          color: '#111827',
          background: 'transparent',
          border: 'none',
          outline: 'none',
          resize: 'vertical',
          fontFamily: 'inherit',
        }}
      />
    </div>
  );
}

export const ChartSlot = ({ title, description }) => {
  const { t } = useTranslation();

  return (
    <div
      style={{
        background: '#f9fafb',
        border: '1.5px dashed #9ca3af',
        borderRadius: 5,
        padding: '14px 18px',
        fontSize: 12,
        color: '#6b7280',
        marginBottom: 10,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
      }}
    >
      <span style={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }}>📊</span>
      <div>
        <div style={{ fontWeight: 700, color: '#111827', marginBottom: 3 }}>
          {title}
        </div>
        {description && <div style={{ lineHeight: 1.55 }}>{description}</div>}
        <div style={{ marginTop: 5, color: '#9ca3af', fontStyle: 'italic' }}>
          {t('report.charts_will_be_available_in_future_update')}
        </div>
      </div>
    </div>
  );
};

export function ChartCaption({ defaultText, onChange }) {
  const [text, setText] = useState(defaultText);
  const handleChange = (e) => {
    setText(e.target.value);
    onChange?.(e.target.value);
  };
  return (
    <textarea
      value={text}
      onChange={handleChange}
      rows={3}
      style={{
        width: '100%',
        boxSizing: 'border-box',
        background: '#fffbeb',
        border: '1px solid #fcd34d',
        borderRadius: 5,
        padding: '10px 14px',
        fontSize: 12,
        color: '#78350f',
        lineHeight: 1.65,
        fontFamily: 'inherit',
        resize: 'vertical',
        marginTop: 8,
        marginBottom: 10,
        display: 'block',
      }}
    />
  );
}

// ── Small helpers ─────────────────────────────────────────────────────────────

export function Stat({ label, value }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          color: '#111827',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: '#111827',
          lineHeight: 1.3,
        }}
      >
        {value}
      </div>
    </div>
  );
}

export function LoadingPanel({ loading }) {
  const { t } = useTranslation();

  return (
    <div
      style={{
        background: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: 5,
        padding: '12px 16px',
        fontSize: 12,
        color: '#9ca3af',
        fontStyle: 'italic',
        marginBottom: 10,
      }}
    >
      {loading ? `${t('common.loading_data')}…` : t('report.no_data_available')}
    </div>
  );
}

// ── Table cell styles ─────────────────────────────────────────────────────────
export const thStyle = {
  textAlign: 'left',
  fontFamily: 'inherit',
  fontWeight: 600,
  padding: '4px 6px',
  color: '#111827',
  fontSize: REPORT_FONT.th,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};
export const thStyleR = {
  textAlign: 'right',
  fontFamily: 'inherit',
  fontWeight: 600,
  padding: '4px 6px',
  color: '#111827',
  fontSize: REPORT_FONT.th,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};
export const thStyleC = {
  textAlign: 'center',
  fontFamily: 'inherit',
  fontWeight: 600,
  padding: '4px 6px',
  color: '#111827',
  fontSize: REPORT_FONT.th,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};
export const tdStyle = {
  textAlign: 'left',
  fontFamily: 'inherit',
  padding: '4px 6px',
  color: '#111827',
};
export const tdStyleR = {
  textAlign: 'right',
  fontFamily: 'inherit',
  padding: '4px 6px',
  color: '#111827',
};
export const tdStyleC = {
  textAlign: 'center',
  fontFamily: 'inherit',
  padding: '4px 6px',
  color: '#111827',
};
